import { FC, useMemo, useState } from 'react';
import { Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
	getOrgStructure,
	orgStructureKeys,
	OrgNode,
} from '@/libs/fleet-api/services/admin-org-structure';
import FleetTitle from '@/libs/antd/components/Title';

// --- Types ---
interface HoverState {
	activeNodeId: number | null;
	relatedIds: Set<number>;
}

// --- Styles (Injected for self-containment) ---
// Horizontal Tree Logic (Left-to-Right)
// Uses Flexbox: li is row (Parent | ChildrenCol), ul is col (Child1, Child2...)
const TREE_STYLES = `
/* Level 1: The root UL */
.org-tree {
    display: flex;
    justify-content: center;
}

.org-tree ul {
	display: flex;
    flex-direction: column; /* Children stack vertically */
    padding-left: 40px; /* Space for connectors */
    position: relative;
    justify-content: center; /* Center children relative to parent */
}

.org-tree li {
	display: flex;
    flex-direction: row; /* Node on left, children on right */
    align-items: center;
    position: relative;
    padding: 10px 0;
}

/* Connectors */

/* Horizontal line from Parent to Children (on the node-group) */
/* We need to draw a line from the right side of the card to the UL */
.node-group {
    position: relative;
    display: flex;
    align-items: center;
    margin-right: 0;
    z-index: 10;
}

/* Vertical Backbone on the UL (Left side) */
/* We use the Children's LI ::before to draw the line back to the spine */

.org-tree li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    border-top: 2px solid #ccc;
    width: 40px; /* matches padding-left of ul */
    height: 1px;
}

/* Vertical Spine: connect the LIs together */
.org-tree li::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    border-left: 2px solid #ccc;
    height: 100%;
}

/* Fix First and Last Child Spine */
.org-tree li:first-child::after {
    top: 50%; /* Start from middle */
    height: 50%;
    bottom: 0;
}

.org-tree li:last-child::after {
    top: 0;
    bottom: 50%;
    height: 50%; /* End at middle */
}

.org-tree li:only-child::after {
    display: none; /* No spine needed for single child */
}

/* Connector from Parent Card to the Spine */
.node-group::after {
    content: '';
    position: absolute;
    right: -40px; /* extend to the children's padding */
    top: 50%;
    width: 40px; 
    border-top: 2px solid #ccc;
    display: none; 
}

/* Fallback for browsers without :has (though most modern do) - we can conditionally add a class in JS */
.node-group.has-children::after {
    display: block;
}

/* --- HIGHTLIGHT LOGIC --- */

/* A. Branch (Descendants): Everything is Blue */
.highlighted-branch > .node-group::after {
    border-color: #1890ff !important;
    border-width: 2px;
}
.highlighted-branch::before {
    border-color: #1890ff !important;
    border-width: 2px;
}
.highlighted-branch::after {
    border-color: #1890ff !important;
    border-width: 2px;
}

/* B. Self (The Hovered Node): Only Outgoing Line (to children) is Blue */
/* Incoming line (::before) and Spine (::after) remain Gray */
/* B. Self (The Hovered Node): Only Outgoing Line (to children) is Blue */
/* Incoming line (::before) and Spine (::after) remain Gray */
.highlighted-self > .node-group::after {
    border-color: #1890ff !important;
    border-width: 2px;
}

/* C. Card Highlight */
.highlighted-card {
    border-color: #1890ff !important;
    border-width: 2px !important;
    z-index: 20;
}


.org-card {
	background: #fff;
	border: 1px solid #d9d9d9;
	padding: 10px;
	display: inline-block;
	border-radius: 8px;
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
	min-width: 180px;
	z-index: 10;
	position: relative;
    text-align: left;
}

.org-card:hover { 
	box-shadow: 0 4px 8px rgba(0,0,0,0.15);
	border-color: #1890ff;
}
`;

// --- Components ---

const NodeCard: FC<{
	node: OrgNode;
	hoverState: HoverState;
	onHover: (node: OrgNode) => void;
	onLeave: () => void;
}> = ({ node, hoverState, onHover, onLeave }) => {
	// Determine state
	const isHoverActive = hoverState.activeNodeId !== null;
	const isRelated = hoverState.relatedIds.has(node.id);
	// User request: "make sure the current hovered parent also has blue border."
	// So we highlight if it is related (which includes the hovered node itself).

	// Styles
	const isDimmed = isHoverActive && !isRelated;
	const isHighlighted = isHoverActive && isRelated;

	return (
		<div
			className={`org-card ${isHighlighted ? 'highlighted-card' : ''}`}
			onMouseEnter={() => onHover(node)}
			onMouseLeave={onLeave}
			style={{
				opacity: isDimmed ? 0.3 : 1,
				transition: 'all 0.3s ease',
				// Highlighted children get a slight scale or shadow bump? optional
				transform: isHighlighted ? 'scale(1.02)' : 'scale(1)',
				zIndex: isHighlighted ? 20 : 10, // Bring to front
			}}
		>
			<div
				style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}
			>
				{node.username}
			</div>
			{/* Display Group Name instead of Code */}
			<div style={{ fontSize: '12px', fontWeight: 'bold', color: '#1890ff' }}>
				{node.groupName}
			</div>
		</div>
	);
};

const OrgTreeNodeComponent: FC<{
	node: OrgNode;
	hoverState: HoverState;
	onHover: (node: OrgNode) => void;
	onLeave: () => void;
}> = ({ node, hoverState, onHover, onLeave }) => {
	const hasChildren = node.children && node.children.length > 0;
	const isRelated = hoverState.relatedIds.has(node.id);
	const isHoverActive = hoverState.activeNodeId !== null;
	const isMainHovered = hoverState.activeNodeId === node.id;

	// Apply highlight class
	let highlightClass = '';
	if (isHoverActive) {
		if (isMainHovered) {
			highlightClass = 'highlighted-self';
		} else if (isRelated) {
			highlightClass = 'highlighted-branch';
		}
	}

	return (
		<li className={highlightClass}>
			<div className={`node-group ${hasChildren ? 'has-children' : ''}`}>
				<NodeCard
					node={node}
					hoverState={hoverState}
					onHover={onHover}
					onLeave={onLeave}
				/>
			</div>
			{hasChildren && (
				<ul>
					{node.children.map((child) => (
						<OrgTreeNodeComponent
							key={child.id}
							node={child}
							hoverState={hoverState}
							onHover={onHover}
							onLeave={onLeave}
						/>
					))}
				</ul>
			)}
		</li>
	);
};

const OrgStructurePage: FC = () => {
	const { data: orgData, isLoading } = useQuery({
		queryKey: orgStructureKeys.all,
		queryFn: getOrgStructure,
	});

	// --- Hover State Management ---
	const [hoverState, setHoverState] = useState<HoverState>({
		activeNodeId: null,
		relatedIds: new Set(),
	});

	const handleNodeHover = (node: OrgNode) => {
		const ids = new Set<number>();
		const collectIds = (n: OrgNode) => {
			ids.add(n.id);
			if (n.children) {
				n.children.forEach(collectIds);
			}
		};
		collectIds(node);
		setHoverState({
			activeNodeId: node.id,
			relatedIds: ids,
		});
	};

	const handleNodeLeave = () => {
		setHoverState({
			activeNodeId: null,
			relatedIds: new Set(),
		});
	};

	const trees = useMemo(() => {
		if (!orgData?.data) return {};

		const roots = orgData.data;

		// Helper to filter roots by code
		const findRoots = (codes: string[]) =>
			roots.filter((n) =>
				codes.some(
					(c) =>
						n.groupCode?.toUpperCase() === c ||
						n.divisionCode?.toUpperCase() === c ||
						n.code?.toUpperCase() === c,
				),
			);

		// 1. BOD & CR
		const bodNodes = findRoots(['BOD', 'CR']);

		// 2. Fleet Heads
		const fleetNodes = findRoots(['FH']);

		// 3. Credit Heads
		const creditNodes = findRoots(['CH']);

		// 4. Others (Includes Ops and Com now)
		const otherNodes = findRoots([
			'FS',
			'SC',
			'SECOM',
			'FCOH',
			'FCDVS',
			'FCAS',
			'FCS',
			'FCOM',
		]);

		return {
			bodNodes,
			fleetNodes,
			creditNodes,
			otherNodes,
		};
	}, [orgData]);

	if (isLoading) {
		return (
			<div style={{ textAlign: 'center', padding: '50px' }}>
				<Spin size="large" />
			</div>
		);
	}

	// Helper to render tree with props
	const renderTree = (nodes?: OrgNode[]) => {
		if (!nodes || nodes.length === 0) return null;
		return (
			<ul>
				{nodes.map((node) => (
					<OrgTreeNodeComponent
						key={node.id}
						node={node}
						hoverState={hoverState}
						onHover={handleNodeHover}
						onLeave={handleNodeLeave}
					/>
				))}
			</ul>
		);
	};

	return (
		<div style={{ width: '100%', overflowX: 'auto', paddingBottom: '50px' }}>
			<style>{TREE_STYLES}</style>
			<div style={{ marginBottom: '24px' }}>
				<FleetTitle level={1}>Organization Structure</FleetTitle>
			</div>

			<div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
				{/* Level 1: Board & CR */}
				{trees.bodNodes && trees.bodNodes.length > 0 && (
					<div className="org-tree">
						<div
							style={{
								textAlign: 'center',
								marginBottom: '10px',
								fontWeight: 'bold',
								color: '#666',
							}}
						>
							Board & Corp Risk
						</div>
						{renderTree(trees.bodNodes)}
					</div>
				)}

				{/* Level 2: Fleet & Credit */}
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: '1fr 1fr',
						gap: '20px',
					}}
				>
					{/* Fleet Column */}
					<div>
						<div
							style={{
								textAlign: 'center',
								marginBottom: '10px',
								fontWeight: 'bold',
								color: '#666',
							}}
						>
							Fleet Division
						</div>
						<div className="org-tree">
							{trees.fleetNodes && trees.fleetNodes.length > 0 ? (
								renderTree(trees.fleetNodes)
							) : (
								<div style={{ textAlign: 'center', color: '#999' }}>
									No Fleet Heads found
								</div>
							)}
						</div>
					</div>

					{/* Credit Column */}
					<div>
						<div
							style={{
								textAlign: 'center',
								marginBottom: '10px',
								fontWeight: 'bold',
								color: '#666',
							}}
						>
							Credit Division
						</div>
						<div className="org-tree">
							{trees.creditNodes && trees.creditNodes.length > 0 ? (
								renderTree(trees.creditNodes)
							) : (
								<div style={{ textAlign: 'center', color: '#999' }}>
									No Credit Heads found
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Level 3: Ops & Commercial (Merged into Others, now Level 3 is Others) */}
				{/* We moved them to Other, so just show Others */}

				{/* Level 4: Others */}
				{trees.otherNodes && trees.otherNodes.length > 0 && (
					<div>
						<div
							style={{
								textAlign: 'center',
								marginBottom: '10px',
								fontWeight: 'bold',
								color: '#666',
							}}
						>
							Others
						</div>
						<div className="org-tree">{renderTree(trees.otherNodes)}</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default OrgStructurePage;
